import React, { useEffect, useReducer, useContext, useMemo } from "react";
import { useTheme } from "@mui/material/styles";
import { getHttpResponse } from "@/api/helper";
import { paymentMethods, accountTypes } from "@/constants";
import { AppContext } from "@/contexts/AppContext";

import MainPageBody from "./mainPageBody";

import mainReducer, { actionTypes, initialState } from "./reducer";
import { parseAmount } from "@/helper/parseAmount";
import { MainPageContext } from "@/contexts/MainPageContext";
import { filterProducts } from "@/helper/filterProducts";
import useGetUserInfoQuery from "@/hooks/queries/useGetUserInfoQuery";
import { getItemPromotionAmount } from "@fripick/cart-utils";
import { getPromotionToApply } from "@/helper/promotions";
import useGetGiftcardsQuery from "@/hooks/queries/useGetGiftcards";
import { calculatePosCheckout } from "@/helper/posCartEngine";
import { getAvailableCreditBalance } from "@/helper/benefitBalance";
import useGetPinCashCardQuery from "@/hooks/queries/useGetPinCashCard";

const MainPage = ({ resetAuthentication }) => {
  const {
    providerId,
    userId,
    token,
    companyId,
    setApplicableBenefits,
    setCompanies,
    setItbis,
    setSubTotal,
    setDiscountAmount,
    companyBranchId,
    setCompanyBranchId,
    selectedEmployee,
    setSelectedEmployee,
    benefits,
    setBenefits,
    companyAccountBenefits,
    setCompanyAccountBenefits,
    setToUseBenefits,
    setAmountToProcess,
    setCashOrCardValue,
    applicableBenefits,
  } = useContext(AppContext);
  const [state, dispatch] = useReducer(mainReducer, initialState);

  const {
    filterProductInputValue,
    users,
    choosenGeneralPromotion,
    catalogList,
    selectedCatalogIndex,
    lastProductsOrdered,
    goToComboPage,
    selectedComboItem,
    selectedCompany,
    catalogTypeId,
    catalogsCurrentDateTime,
    initialTimeLeft,
    timeLeft,
    categories,
    categoriesSelected,
    alert,
    isLoading,
    openModal,
    selectedLetter,
    selectedAccountTypeId,
    selectedPaymentMethod,
    availableBalanceToday,
    openNotEnoughBalanceMessage,
    cartAlert,
    selectedProducts,
  } = state;

  const theme = useTheme();
  const [promotions, setPromotions] = React.useState([]);
  const { data: userInfo, isFetched: isUserInfoFetched } =
    useGetUserInfoQuery();
  const {data: pinCashCardResponse, isSuccess: isPinCashCardReady} = useGetPinCashCardQuery(selectedCompany?.companyId);
  const { data: giftcardResponse, isSuccess: areGiftcardsReady } =
    useGetGiftcardsQuery(selectedEmployee.id); 
  const [selectedProductsWithPromotions, setSelectedProductsWithPromotions] =
    React.useState([]);
  const [usedGiftCards, setUsedGiftCards] = React.useState([]);
  const [checkoutTotals, setCheckoutTotals] = React.useState({
    grossSubtotal: 0,
  });
  const [checkoutAccepted, setCheckoutAccepted] = React.useState(false);
  const [financialDataReady, setFinancialDataReady] = React.useState(false);
  const financialRequestId = React.useRef(0);
  const catalogRequestId = React.useRef(0);
  const companyRequestId = React.useRef(0);
  const checkoutReady = financialDataReady && areGiftcardsReady;
  const checkoutOutputSetters = React.useRef({
    setAmountToProcess,
    setCashOrCardValue,
    setDiscountAmount,
    setItbis,
    setSubTotal,
    setToUseBenefits,
  });
  const catalog = useMemo(() => {
    var catalogItems =
      catalogList?.length > 0 ? catalogList[selectedCatalogIndex]?.items : [];
    catalogItems = catalogItems.map((item) => {
      if (selectedAccountTypeId === accountTypes.company) {
        return item;
      }
      var selectedPromotion = getPromotionToApply(
        item.id,
        promotions,
        selectedProducts,
      );
      var promotionDiscountAmount = 0;
      promotionDiscountAmount = getItemPromotionAmount(
        parseAmount(item.price),
        1,
        selectedPromotion,
      );
      return {
        ...item,
        promotion: selectedPromotion,
        discountAmount: promotionDiscountAmount,
      };
    });
    return catalogItems;
  }, [
    promotions,
    catalogList,
    selectedCatalogIndex,
    selectedAccountTypeId,
    selectedProducts,
  ]);
  const setAlert = (payload) => {
    dispatch({ type: actionTypes.setAlert, payload });
  };

  const setOpenModal = (payload) => {
    dispatch({ type: actionTypes.setOpenModal, payload });
  };

  // --------------------Logic to set timer and and update it ---------------------------

  useEffect(() => {
    if (
      catalogsCurrentDateTime &&
      catalogList[selectedCatalogIndex]?.dateTimeClose
    ) {
      const secondsDifference = Math.floor(
        (new Date(catalogList[selectedCatalogIndex].dateTimeClose) -
          new Date(catalogsCurrentDateTime)) /
          1000,
      );

      if (secondsDifference > 0) {
        dispatch({ type: actionTypes.setTimers, payload: secondsDifference });
      }
    }
  }, [catalogsCurrentDateTime, selectedCatalogIndex, catalogList]);

  useEffect(() => {
    let interval;
    if (initialTimeLeft) {
      function substractSecond() {
        dispatch({ type: actionTypes.reduceTimeLeft });
      }
      interval = setInterval(substractSecond, 1000);
    }
    return () => clearInterval(interval);
  }, [initialTimeLeft]);

  useEffect(() => {
    if (timeLeft === 0) dispatch({ type: actionTypes.clearCatalog });
  }, [timeLeft]);

  //Set available balance
  useEffect(() => {
    const thisBenefits =
      selectedAccountTypeId === accountTypes.employee
        ? benefits
        : selectedAccountTypeId === accountTypes.company
          ? companyAccountBenefits
          : null;
    dispatch({
      type: actionTypes.setAvailableBalanceToday,
      payload: getAvailableCreditBalance(thisBenefits),
    });
  }, [benefits, companyAccountBenefits, selectedAccountTypeId]);

  const secondsToTimeString = (totalSeconds) => {
    if (totalSeconds <= 0 || isNaN(totalSeconds)) {
      return "00:00:00";
    }
    let hours = Math.floor(totalSeconds / 3600);
    let minutes = Math.floor((totalSeconds - hours * 3600) / 60);
    let seconds = totalSeconds - hours * 3600 - minutes * 60;

    hours = hours < 10 ? `0${hours}` : hours;
    minutes = minutes < 10 ? `0${minutes}` : minutes;
    seconds = seconds < 10 ? `0${seconds}` : seconds;

    return `${hours}:${minutes}:${seconds}`;
  };

  const setupUserCompanies = async () => {
    try {
      const responseCompanies = (userInfo?.data?.companies || []).map(
        (company) => ({
          companyId: company.id,
          companyName: company.companyName,
        }),
      );
      setCompanies(responseCompanies);
      const foundPreselectedCompany = responseCompanies.find(
        (item) => item.companyId === parseFloat(companyId),
      );
      const selectedCompany = foundPreselectedCompany ?? responseCompanies[0];
      setSelectedEmployee({});
      dispatch({
        type: actionTypes.setSelectedCompany,
        payload: selectedCompany,
      });
      fetchUsers(selectedCompany.companyId, ++companyRequestId.current);
    } catch (error) {
      dispatch({
        type: actionTypes.setAlert,
        payload: {
          open: true,
          severity: "error",
          message: error?.message || defaultErrorMessage,
          duration: defaultAlertDuration,
        },
      });
    }
  };

  // ---------------------------- http requests -----------------------------------------------
  const defaultErrorMessage = "Ocurrió un error buscando los datos";
  const defaultAlertDuration = 3000;

  const httpResponseHelper = async (url) => {
    const response = await getHttpResponse(url, token, userId);
    return response;
  };

  const financialHttpResponse = async (url) => {
    try {
      return await httpResponseHelper(url);
    } catch {
      return httpResponseHelper(url);
    }
  };

  const fetchUsers = async (companyId, requestId) => {
    dispatch({ type: actionTypes.setIsLoading, payload: true });
    try {
      const response = await httpResponseHelper(
        `company-service/company/${companyId}/users`,
      );
      if (companyRequestId.current === requestId) {
        dispatch({ type: actionTypes.setUsers, payload: response.data.users });
      }
    } catch (error) {
      if (companyRequestId.current === requestId) {
        dispatch({
          type: actionTypes.setAlert,
          payload: {
            open: true,
            severity: "error",
            message: error?.message || defaultErrorMessage,
            duration: defaultAlertDuration,
          },
        });
      }
    }
    if (companyRequestId.current === requestId) {
      dispatch({ type: actionTypes.setIsLoading, payload: false });
    }
  };

  const fetchCategories = async (providerId) => {
    dispatch({ type: actionTypes.setIsLoading, payload: true });
    try {
      const response = await httpResponseHelper(
        `category-service/provider/${providerId}/categories`,
      );
      dispatch({ type: actionTypes.setCategories, payload: response.data });
      const categoriesSelectedObj = {};
      response.data.forEach(
        (category) => (categoriesSelectedObj[category.name] = false),
      );
      dispatch({
        type: actionTypes.setCategoriesSelected,
        payload: categoriesSelectedObj,
      });
    } catch (error) {
      dispatch({
        type: actionTypes.setAlert,
        payload: {
          open: true,
          severity: "error",
          message: error?.message || defaultErrorMessage,
          duration: defaultAlertDuration,
        },
      });
    }
    dispatch({ type: actionTypes.setIsLoading, payload: false });
  };

  const fetchCatalogByUserId = async (userId, providerId, requestId) => {
    dispatch({ type: actionTypes.setIsLoading, payload: true });
    try {
      const companyBranchresponse = await httpResponseHelper(
        `company-service/users/${userId}/company-branch`,
      );
      if (catalogRequestId.current !== requestId) return;
      setCompanyBranchId(companyBranchresponse.data.companyBranchId);
      const catalogResponse = await httpResponseHelper(
        `catalog-pos-service/provider/${providerId}/catalogs?userid=${userId}&companybranchid=${companyBranchresponse.data.companyBranchId}`,
      );
      if (catalogRequestId.current !== requestId) return;
      const catalogData = catalogResponse.data.catalogs;
      if (catalogData.length === 0) {
        dispatch({ type: actionTypes.setCatalogList, payload: [] });
        dispatch({ type: actionTypes.setCatalogTypeId, payload: undefined });
        dispatch({
          type: actionTypes.setCatalogsCurrentDateTime,
          payload: undefined,
        });
      } else {
        dispatch({ type: actionTypes.setCatalogList, payload: catalogData });
        dispatch({
          type: actionTypes.setCatalogsCurrentDateTime,
          payload: catalogResponse.data.currentDateTime,
        });
        dispatch({ type: actionTypes.setSelectedCatalogIndex, payload: 0 });
        dispatch({
          type: actionTypes.setCatalogTypeId,
          payload: catalogData[0].catalogTypeId,
        });
      }
    } catch (error) {
      if (catalogRequestId.current === requestId) {
        dispatch({
          type: actionTypes.setAlert,
          payload: {
            open: true,
            severity: "error",
            message: error?.message || defaultErrorMessage,
            duration: defaultAlertDuration,
          },
        });
      }
    } finally {
      if (catalogRequestId.current === requestId) {
        dispatch({ type: actionTypes.setIsLoading, payload: false });
      }
    }
  };

  const fetchPromotions = async (userId, providerId, requestId) => {
    try {
      const response = await financialHttpResponse(
        `/promotion-service/users/${userId}/promotions?providerId=${providerId}`,
      );
      if (financialRequestId.current === requestId)
        setPromotions(response.data);
      return true;
    } catch (error) {
      dispatch({
        type: actionTypes.setAlert,
        payload: {
          open: true,
          severity: "error",
          message: error?.message || defaultErrorMessage,
          duration: defaultAlertDuration,
        },
      });
      return false;
    }
  };

  const fetchApplicableBenefits = async (userId, requestId) => {
    try {
      const response = await financialHttpResponse(
        `benefit-service/applicableposbenefits?providerId=${providerId}&userId=${userId}&companyId=${selectedCompany?.companyId}&companyBranchId=${companyBranchId}`,
      );
      if (financialRequestId.current === requestId)
        setApplicableBenefits(response.data);
      return true;
    } catch (error) {
      dispatch({
        type: actionTypes.setAlert,
        payload: {
          open: true,
          severity: "error",
          message: error?.message || defaultErrorMessage,
          duration: defaultAlertDuration,
        },
      });
      return false;
    }
  };

  const fetchUserBenefits = async (userId, catalogTypeId, requestId) => {
    try {
      const response = await financialHttpResponse(
        `benefit-service/user/${userId}/posbenefits?catalogTypeId=${catalogTypeId}&accountTypeId=1`,
      );
      if (financialRequestId.current === requestId) setBenefits(response.data);
      return true;
    } catch (error) {
      dispatch({
        type: actionTypes.setAlert,
        payload: {
          open: true,
          severity: "error",
          message: error?.message || defaultErrorMessage,
          duration: defaultAlertDuration,
        },
      });
      return false;
    }
  };

  const fetchCompanyAccountBenefits = async (
    userId,
    catalogTypeId,
    requestId,
  ) => {
    try {
      const response = await financialHttpResponse(
        `benefit-service/user/${userId}/posbenefits?catalogTypeId=${catalogTypeId}&accountTypeId=2`,
      );
      if (financialRequestId.current === requestId)
        setCompanyAccountBenefits(response.data);
      return true;
    } catch (error) {
      dispatch({
        type: actionTypes.setAlert,
        payload: {
          open: true,
          severity: "error",
          message: error?.message || defaultErrorMessage,
          duration: defaultAlertDuration,
        },
      });
      return false;
    }
  };

  const fetchLatestItems = async (userId, providerId, requestId) => {
    try {
      const response = await httpResponseHelper(
        `catalog-pos-service/provider/${providerId}/latest-items?userId=${userId}`,
      );
      if (catalogRequestId.current === requestId) {
        dispatch({
          type: actionTypes.setLastProductsOrdered,
          payload: response.data,
        });
      }
    } catch (error) {
      dispatch({
        type: actionTypes.setAlert,
        payload: {
          open: true,
          severity: "error",
          message: error?.message || defaultErrorMessage,
          duration: defaultAlertDuration,
        },
      });
    }
  };

  useEffect(() => {
    if (userInfo !== undefined && isUserInfoFetched) {
      setupUserCompanies(providerId);
    }
  }, [isUserInfoFetched]);
  useEffect(() => {
    fetchCategories(providerId);
  }, []);

  useEffect(() => {
    const requestId = ++financialRequestId.current;
    setFinancialDataReady(false);
    setBenefits();
    setCompanyAccountBenefits();
    setApplicableBenefits();
    setPromotions([]);

    if (!selectedEmployee.id || !catalogTypeId) return;

    const requests = [
      fetchUserBenefits(selectedEmployee.id, catalogTypeId, requestId),
      fetchPromotions(selectedEmployee.id, providerId, requestId),
      fetchApplicableBenefits(selectedEmployee.id, requestId),
    ];
    if (selectedEmployee.companyAccount) {
      requests.push(
        fetchCompanyAccountBenefits(
          selectedEmployee.id,
          catalogTypeId,
          requestId,
        ),
      );
    }
    Promise.all(requests).then((results) => {
      if (financialRequestId.current === requestId)
        setFinancialDataReady(results.every(Boolean));
    });
  }, [selectedEmployee.id, selectedEmployee.companyAccount, catalogTypeId]);

  // ---------------------------- Functions ------------------------------------------------------

  const setFilterProductInputValueHandler = (value) => {
    dispatch({ type: actionTypes.setSelectedLetter, payload: null });
    dispatch({ type: actionTypes.setFilterProductInputValue, payload: value });
  };

  const setSelectedLetterHandler = (letter) => {
    dispatch({ type: actionTypes.setFilterProductInputValue, payload: "" });
    dispatch({ type: actionTypes.setSelectedLetter, payload: letter });
  };

  const toggleCategory = (categoryName) => {
    const previousCategoriesSelectedCopy = { ...categoriesSelected };
    previousCategoriesSelectedCopy[categoryName] =
      !previousCategoriesSelectedCopy[categoryName];
    dispatch({
      type: actionTypes.setCategoriesSelected,
      payload: previousCategoriesSelectedCopy,
    });
  };

  const changeCatalogIndexHandler = (index) => {
    dispatch({ type: actionTypes.setSelectedCatalogIndex, payload: index });
    dispatch({
      type: actionTypes.setCatalogTypeId,
      payload: catalogList[index].catalogTypeId,
    });
  };

  const notEnoughBalanceHandler = () => {
    dispatch({
      type: actionTypes.setOpenNotEnoughBalanceMessage,
      payload: true,
    });
    setTimeout(
      () =>
        dispatch({
          type: actionTypes.setOpenNotEnoughBalanceMessage,
          payload: false,
        }),
      3000,
    );
  };

  const calculateCheckoutForProducts = (
    products,
    selectedGlobalPromotion = choosenGeneralPromotion,
  ) =>
    calculatePosCheckout({
      accountTypeId: selectedAccountTypeId,
      applicableBenefits,
      availableBalance: availableBalanceToday,
      benefits:
        selectedAccountTypeId === accountTypes.employee
          ? benefits?.benefitsOrder
          : companyAccountBenefits?.benefitsOrder,
      giftcardResponse,
      currentDateTime: catalogsCurrentDateTime,
      paymentMethod: selectedPaymentMethod,
      products,
      selectedGlobalPromotion,
    });

  const canUseProducts = (
    products,
    { allowRejectedReduction = false } = {},
  ) => {
    if (!checkoutReady) {
      setAlert({
        open: true,
        severity: "info",
        message: "Los beneficios y promociones todavía se están cargando",
        duration: defaultAlertDuration,
      });
      return false;
    }
    const checkout = calculateCheckoutForProducts(products);
    const accepted = checkout.payment.accepted;
    if (
      allowRejectedReduction &&
      Number.isFinite(checkoutTotals.amountDue) &&
      checkout.totals.amountDue < checkoutTotals.amountDue
    ) {
      return true;
    }
    if (!accepted) notEnoughBalanceHandler();
    return accepted;
  };

  const isGeneralPromotionApplicable = (promotion) => {
    if (!checkoutReady) return false;
    const checkout = calculateCheckoutForProducts(selectedProducts, promotion);
    return checkout.selectedGlobalPromotion?.promoId === promotion.promoId;
  };

  useEffect(() => {
    if (selectedProducts.length > 0 && !checkoutReady) {
      const setters = checkoutOutputSetters.current;
      setters.setSubTotal(0);
      setters.setDiscountAmount(0);
      setters.setItbis({});
      setters.setToUseBenefits({});
      setters.setAmountToProcess(0);
      setters.setCashOrCardValue(0);
      setSelectedProductsWithPromotions([]);
      setUsedGiftCards([]);
      setCheckoutTotals({ grossSubtotal: 0 });
      setCheckoutAccepted(false);
      dispatch({ type: actionTypes.setOpenModal, payload: false });
      return;
    }
    const checkout = calculatePosCheckout({
      accountTypeId: selectedAccountTypeId,
      applicableBenefits,
      availableBalance: availableBalanceToday,
      benefits:
        selectedAccountTypeId === accountTypes.employee
          ? benefits?.benefitsOrder
          : companyAccountBenefits?.benefitsOrder,
      giftcardResponse,
      currentDateTime: catalogsCurrentDateTime,
      paymentMethod: selectedPaymentMethod,
      products: selectedProducts,
      selectedGlobalPromotion: choosenGeneralPromotion,
    });
    const taxes = Object.fromEntries(
      (checkout.totals.taxesList ?? []).map((tax) => [tax.name, tax.amount]),
    );
    const setters = checkoutOutputSetters.current;

    setters.setSubTotal(checkout.totals.subtotalAfterPromotions);
    setters.setDiscountAmount(checkout.totals.totalPromotion);
    setters.setItbis(taxes);
    setters.setToUseBenefits({
      asignacion: checkout.totals.assignment,
      asignacionDiaria: checkout.totals.dailyAssignment,
      giftcard: checkout.totals.giftCard,
      subsidio: checkout.totals.subsidy,
    });
    setters.setAmountToProcess(checkout.payment.primaryTenderAmount);
    setters.setCashOrCardValue(
      checkout.payment.accepted ? checkout.payment.secondaryTenderAmount : 0,
    );
    setSelectedProductsWithPromotions(checkout.displayProducts);
    setUsedGiftCards(checkout.usedGiftCards);
    setCheckoutTotals(checkout.totals);
    setCheckoutAccepted(
      selectedProducts.length > 0 && checkoutReady && checkout.payment.accepted,
    );

    if (choosenGeneralPromotion && !checkout.selectedGlobalPromotion) {
      dispatch({ type: actionTypes.setChoosenGeneralPromotion, payload: null });
    }
    if (selectedProducts.length > 0 && !checkout.payment.accepted) {
      dispatch({ type: actionTypes.setOpenModal, payload: false });
      dispatch({
        type: actionTypes.setOpenNotEnoughBalanceMessage,
        payload: true,
      });
      const timeout = setTimeout(
        () =>
          dispatch({
            type: actionTypes.setOpenNotEnoughBalanceMessage,
            payload: false,
          }),
        3000,
      );
      return () => clearTimeout(timeout);
    }
  }, [
    selectedProducts,
    choosenGeneralPromotion,
    selectedAccountTypeId,
    selectedPaymentMethod,
    availableBalanceToday,
    benefits,
    companyAccountBenefits,
    applicableBenefits,
    giftcardResponse,
    checkoutReady,
    catalogsCurrentDateTime,
  ]);

  const addProductToCart = (product) => {
    const currentCatalog = catalogList[selectedCatalogIndex];
    const selectedProductsCopy = selectedProducts.map((prod) => {
      return { ...prod };
    });
    const foundProductIndex = selectedProductsCopy.findIndex(
      (prod) => prod.id === product.id,
    );

    if (foundProductIndex >= 0) {
      const foundProductCopy = { ...selectedProductsCopy[foundProductIndex] };
      foundProductCopy.quantity++;
      selectedProductsCopy[foundProductIndex] = foundProductCopy;
    } else {
      selectedProductsCopy.push({
        ...product,
        applyServiceTax: product.applyServiceTax ?? currentCatalog.taxService,
        cashOrCardValue: 0,
        deliveryDate: currentCatalog.deliveryDate,
        deliveryTypeId: currentCatalog.deliveryTypes[0].id,
        orderReason: "",
        providerId,
        quantity: 1,
        sections: [],
        userType: selectedAccountTypeId,
      });
    }

    if (canUseProducts(selectedProductsCopy)) {
      dispatch({
        type: actionTypes.setSelectedProducts,
        payload: selectedProductsCopy,
      });
    }
  };

  const editProductQuantity = (productId, quantity, isCombo) => {
    if (quantity < 0) return;
    const selectedProductsCopy = selectedProducts.map((prod) => {
      return { ...prod };
    });
    const foundProductIndex = selectedProductsCopy.findIndex((prod) =>
      isCombo ? prod.key === productId : prod.id === productId,
    );
    const foundProductCopy = { ...selectedProductsCopy[foundProductIndex] };
    foundProductCopy.quantity = quantity;
    selectedProductsCopy[foundProductIndex] = foundProductCopy;

    if (
      canUseProducts(selectedProductsCopy, { allowRejectedReduction: true })
    ) {
      dispatch({
        type: actionTypes.setSelectedProducts,
        payload: selectedProductsCopy,
      });
    }
  };

  const handleZeroQuantity = () => {
    if (selectedProducts.some((product) => product.quantity < 1)) {
      const selectedProductsCopy = selectedProducts.map((prod) => {
        return { ...prod, quantity: prod.quantity < 1 ? "1" : prod.quantity };
      });
      if (canUseProducts(selectedProductsCopy)) {
        dispatch({
          type: actionTypes.setSelectedProducts,
          payload: selectedProductsCopy,
        });
      }
    }
  };

  const deleteProductCart = (productId) => {
    const selectedProductsFilter = selectedProducts
      .map((prod) => {
        return { ...prod };
      })
      .filter((product) => product.id !== productId); //copying and filtering
    if (
      canUseProducts(selectedProductsFilter, { allowRejectedReduction: true })
    ) {
      dispatch({
        type: actionTypes.setSelectedProducts,
        payload: selectedProductsFilter,
      });
    }
  };

  const deleteComboCart = (combo) => {
    const selectedProductsFilter = selectedProducts
      .map((prod) => {
        return { ...prod };
      })
      .filter((product) => {
        //copying and filtering
        if (!product.selectedProductIndexes) return true;
        if (combo.id !== product.id) return true;
        if (combo.selectedProductIndexes === product.selectedProductIndexes)
          return false;
        return true;
      });

    if (
      canUseProducts(selectedProductsFilter, { allowRejectedReduction: true })
    ) {
      dispatch({
        type: actionTypes.setSelectedProducts,
        payload: selectedProductsFilter,
      });
    }
  };

  const comboSelectedHandler = (comboItem) => {
    dispatch({ type: actionTypes.setGoToComboPage, payload: true });
    dispatch({ type: actionTypes.setSelectedComboItem, payload: comboItem });
  };

  const clearComboPage = () => {
    dispatch({ type: actionTypes.setGoToComboPage, payload: false });
  };

  const addComboToCart = (combo) => {
    const currentCatalog = catalogList[selectedCatalogIndex];
    const selectedProductsCopy = selectedProducts.map((prod) => {
      return { ...prod };
    });
    let foundSameCombo = false;
    const foundCombos = selectedProductsCopy.filter(
      (thisCombo) => combo.id === thisCombo.id,
    );
    let foundCombo;
    if (foundCombos.length > 0) {
      for (let i = 0; i < foundCombos.length; i++) {
        foundCombo = foundCombos[i];
        if (foundCombo.sections?.length === combo.sections?.length) {
          foundSameCombo = true;
          foundCombo.sections.forEach((section) => {
            const sameSections = combo.sections.filter(
              (thisSection) => thisSection.id === section.id,
            );
            if (sameSections.length === 0) {
              foundSameCombo = false;
              return;
            } else if (
              !sameSections.some(
                (sameSection) => sameSection.variantId === section.variantId,
              )
            ) {
              foundSameCombo = false;
              return;
            }
          });
          if (foundSameCombo) break;
        }
      }

      if (foundSameCombo === true) {
        foundCombo.quantity++;
      }
    }

    if (!foundSameCombo) {
      selectedProductsCopy.push({
        ...combo,
        applyServiceTax: combo.applyServiceTax ?? currentCatalog.taxService,
        cashOrCardValue: 0,
        deliveryDate: currentCatalog.deliveryDate,
        deliveryTypeId: currentCatalog.deliveryTypes[0].id,
        key: Date.now(),
        orderReason: "",
        providerId,
        userType: selectedAccountTypeId,
      });
    }

    if (canUseProducts(selectedProductsCopy)) {
      dispatch({
        type: actionTypes.setSelectedProducts,
        payload: selectedProductsCopy,
      });
    }
  };

  const addComboToCartHandler = (combo) => {
    addComboToCart(combo);
    dispatch({ type: actionTypes.setGoToComboPage, payload: false });
  };

  const clearCart = () => {
    dispatch({ type: actionTypes.setSelectedProducts, payload: [] });
    setSubTotal(0);
    setItbis({});
  };

  const commonClear = () => {
    clearCart();
    dispatch({
      type: actionTypes.setSelectedAccountTypeId,
      payload: accountTypes.employee,
    });
    dispatch({ type: actionTypes.commonClear });
  };

  const clearSelectedEmployee = () => {
    catalogRequestId.current++;
    commonClear();
    setSelectedEmployee({});
  };

  const changeCompanyHandler = (newValue) => {
    catalogRequestId.current++;
    const requestId = ++companyRequestId.current;
    dispatch({ type: actionTypes.setSelectedCompany, payload: newValue });
    dispatch({
      type: actionTypes.setSelectedPaymentMethod,
      payload: paymentMethods.nomina,
    });
    commonClear();
    setSelectedEmployee({});
    dispatch({ type: actionTypes.setUsers, payload: [] });
    fetchUsers(newValue.companyId, requestId);
  };

  const changeEmployeeHandler = (newValue) => {
    const requestId = ++catalogRequestId.current;
    commonClear();
    setSelectedEmployee(newValue);
    changePaymentMethodHandler(paymentMethods.nomina);
    fetchCatalogByUserId(newValue.id, providerId, requestId);
    fetchLatestItems(newValue.id, providerId, requestId);
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    dispatch({ type: actionTypes.setAlert, payload: { open: false } });
  };

  const handleCloseCartAlert = () => {
    dispatch({
      type: actionTypes.setCartAlert,
      payload: { function: null, text: null },
    });
  };

  const changeAccountTypeHandler = (id) => {
    const theFunction = () => {
      if (id === accountTypes.company) {
        dispatch({
          type: actionTypes.setSelectedPaymentMethod,
          payload: paymentMethods.nomina,
        });
      }
      dispatch({ type: actionTypes.setSelectedAccountTypeId, payload: id });
    };

    if (!selectedProducts || selectedProducts.length === 0) theFunction();
    else
      dispatch({
        type: actionTypes.setCartAlert,
        payload: {
          function: () => {
            theFunction();
            dispatch({
              type: actionTypes.setCartAlert,
              payload: { function: null, text: null },
            });
            clearCart();
          },
          text: id === accountTypes.company ? "Cuenta Empresa" : "Nómina",
        },
      });
  };

  const changePaymentMethodHandler = (value) => {
    const theFunction = () => {
      dispatch({ type: actionTypes.setSelectedPaymentMethod, payload: value });
    };

    if (!selectedProducts || selectedProducts.length === 0) theFunction();
    else
      dispatch({
        type: actionTypes.setCartAlert,
        payload: {
          function: () => {
            theFunction();
            dispatch({
              type: actionTypes.setCartAlert,
              payload: { function: null, text: null },
            });
            clearCart();
          },
          text:
            value === paymentMethods.nomina
              ? "Nómina"
              : value === paymentMethods.efectivo
                ? "Efectivo"
                : "Tarjeta",
        },
      });
  };
  const filteredCatalogsProducts = useMemo(() => {
    return filterProducts(
      catalog,
      categoriesSelected,
      filterProductInputValue,
      selectedLetter,
    );
  }, [
    catalog,
    filterProductInputValue,
    categoriesSelected,
    selectedLetter,
    promotions,
  ]);

  const contextValues = {
    filterProductInputValue,
    selectedProducts,
    users,
    lastProductsOrdered,
    theme,
    goToComboPage,
    selectedComboItem,
    selectedCompany,
    addProductToCart,
    deleteComboCart,
    editProductQuantity,
    deleteProductCart,
    comboSelectedHandler,
    clearComboPage,
    addComboToCartHandler,
    changeCompanyHandler,
    changeEmployeeHandler,
    setFilterProductInputValue: setFilterProductInputValueHandler,
    catalogList,
    selectedCatalogIndex,
    setSelectedCatalogIndex: changeCatalogIndexHandler,
    timeLeft: timeLeft ? secondsToTimeString(timeLeft) : "00:00:00",
    filteredCatalogsProducts,
    categories,
    categoriesSelected,
    toggleCategory,
    alert,
    handleCloseAlert,
    setAlert,
    isLoading,
    openModal,
    setOpenModal,
    selectedLetter,
    setSelectedLetter: setSelectedLetterHandler,
    setSelectedAccountTypeId: changeAccountTypeHandler,
    selectedAccountTypeId,
    selectedPaymentMethod,
    setSelectedPaymentMethod: changePaymentMethodHandler,
    openNotEnoughBalanceMessage,
    cartAlert,
    handleCloseCartAlert,
    handleZeroQuantity,
    clearSelectedEmployee,
    promotions,
    choosenGeneralPromotion,
    availableBalanceToday,
    checkoutAccepted,
    checkoutReady,
    checkoutTotals,
    noPinCashCard: pinCashCardResponse?.data?.noPinCashCard ?? false,
    isPinCashCardReady,
    isGeneralPromotionApplicable,
    selectedProductsWithPromotions,
    setChoosenGeneralPromotion: (promotion) =>
      dispatch({
        type: actionTypes.setChoosenGeneralPromotion,
        payload: promotion,
      }),
    giftCards: giftcardResponse?.data?.giftCards ?? [],
    usedGiftCards,
  };

  return (
    <MainPageContext.Provider value={contextValues}>
      <MainPageBody />
    </MainPageContext.Provider>
  );
};

export default MainPage;
